import React, { useState } from 'react'
import { useParams } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { getSiteChecklistsForCompany, reviewSiteChecklist, fetchSiteChecklistPdf } from 'services/clients.svc'
import { EmptyList } from 'components'
import { PageTitle } from 'context/PageTitleContext'
import { mediumTime } from 'common/constants'
import './SiteChecklists.scss'

async function openOrDownloadPdf(companyId, checklistId, download) {
  try {
    const res = await fetchSiteChecklistPdf(companyId, checklistId)
    if (!res.ok) throw new Error('PDF not available')
    const blob = await res.blob()
    const objectUrl = URL.createObjectURL(blob)
    if (!download) {
      window.open(objectUrl, '_blank', 'noopener,noreferrer')
    } else {
      const a = document.createElement('a')
      a.href = objectUrl
      a.download = `site-inspection-checklist-${checklistId}.pdf`
      a.click()
    }
    setTimeout(() => URL.revokeObjectURL(objectUrl), 10000)
  } catch (e) {
    console.error('PDF action failed', e)
    alert('PDF is not yet available. Please try again shortly.')
  }
}

const QUERY_KEY = 'siteChecklists'

// Group checklists by month label e.g. "April 2026"
function groupByMonth(checklists) {
  const groups = {}
  checklists.forEach((c) => {
    const d = new Date(c.createdAt)
    const label = d.toLocaleString('en-AU', { month: 'long', year: 'numeric' })
    if (!groups[label]) groups[label] = []
    groups[label].push(c)
  })
  return groups
}

function MonthGroup({ label, items, companyId, onToggleReviewed }) {
  const [open, setOpen] = useState(true)
  const unreviewedCount = items.filter((c) => !c.reviewedAt).length

  return (
    <div className="month-group">
      <div className="month-header" onClick={() => setOpen((v) => !v)}>
        <span className="chevron">{open ? '▼' : '▶'}</span>
        <span>{label}</span>
        {unreviewedCount > 0 && (
          <span className="unreviewed-badge">{unreviewedCount} unreviewed</span>
        )}
      </div>
      {open && (
        <div className="table-container">
          <table>
            <thead>
              <tr>
                <th>Submitted By</th>
                <th>Site Address</th>
                <th>Job No.</th>
                <th>Date</th>
                <th>View / Download</th>
                <th>Review Status</th>
              </tr>
            </thead>
            <tbody>
              {items.map((c) => {
                const submittedBy = c.preparedBy || '—'
                const siteAddress = c.siteAddress || c.site?.address || '—'
                const jobNo = c.site?.siteNumber || '—'
                return (
                  <tr key={c._id}>
                    <td>{submittedBy}</td>
                    <td>{siteAddress}</td>
                    <td>{jobNo}</td>
                    <td>{mediumTime.format(new Date(c.createdAt))}</td>
                    <td className="pdf-actions">
                      <span
                        className="pdf-link"
                        onClick={() => openOrDownloadPdf(companyId, c._id, false)}
                      >
                        View PDF
                      </span>
                      <span className="pdf-link-sep">|</span>
                      <span
                        className="pdf-link"
                        onClick={() => openOrDownloadPdf(companyId, c._id, true)}
                      >
                        Download PDF
                      </span>
                    </td>
                    <td className="review-status-cell">
                      {c.reviewedAt ? (
                        <div className="reviewed-on-text">
                          <span className="reviewed-on-date">✓ {mediumTime.format(new Date(c.reviewedAt))}</span>
                          {c.reviewedByDetails && (
                            <span className="reviewed-on-by">{c.reviewedByDetails.firstName} {c.reviewedByDetails.surname}</span>
                          )}
                        </div>
                      ) : (
                        <input
                          type="checkbox"
                          checked={false}
                          title="Mark as reviewed"
                          onChange={() => onToggleReviewed(c._id, true)}
                        />
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

export default function SiteChecklists() {
  const { companyId } = useParams()
  const queryClient = useQueryClient()

  const { data: checklists = [] } = useQuery(
    [QUERY_KEY, companyId],
    () => getSiteChecklistsForCompany(companyId),
  )

  const { mutate: toggleReviewed } = useMutation(
    ({ checklistId, reviewed }) => reviewSiteChecklist(companyId, checklistId, reviewed),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([QUERY_KEY, companyId])
        queryClient.invalidateQueries(['unreviewed-checklists-count', companyId])
      },
    },
  )

  const grouped = groupByMonth(checklists)
  const months = Object.keys(grouped)

  return (
    <div className="checklist-container body-container">
      <PageTitle title="Checklists" />
      <h2>Site Inspection Checklists</h2>

      {months.length === 0 ? (
        <EmptyList />
      ) : (
        months.map((month) => (
          <MonthGroup
            key={month}
            label={month}
            items={grouped[month]}
            companyId={companyId}
            onToggleReviewed={(checklistId, reviewed) =>
              toggleReviewed({ checklistId, reviewed })
            }
          />
        ))
      )}
    </div>
  )
}
